import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { CssBaseline } from '@mui/material';
import Sidebar from './components/Sidebar';
import OAuthCallback from './components/OAuthCallback';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AllSites from './pages/AllSites';
import AddSite from './pages/AddSite';
import SiteEdit from './pages/SiteEdit';
import SiteForms from './pages/SiteForms';
import AdminDatabase from './pages/AdminDatabase';
import DisallowedList from './pages/DisallowedList';
import SiteHealth from './pages/SiteHealth';
import Forms from './pages/Forms';
import ListSiteForms from './pages/ListSiteForms';
import Logs from './pages/Logs';
import Contacts from './pages/Contacts';
import ContactLinkReview from './pages/ContactLinkReview';
import Subscriptions from './pages/Subscriptions';
import Domains from './pages/Domains';
import DomainDetail from './pages/DomainDetail';
import WPEnginePage from './pages/WPEnginePage';
import { AuthProvider, useAuth } from './services/AuthContext';
import { CustomThemeProvider } from './contexts/ThemeContext';

function LoginWithTheme() {
  return (
    <CustomThemeProvider>
      <CssBaseline />
      <Login />
    </CustomThemeProvider>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  // Also check localStorage directly as a fallback
  const hasToken = localStorage.getItem('access_token');
  
  // Show loading while auth is being determined
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (isAuthenticated || hasToken) ? children : <Navigate to="/login" />;
}

function RootHandler() {
  // Check if this is an OAuth callback
  const urlParams = new URLSearchParams(window.location.search);
  const hasOAuthCode = urlParams.get('code');
  
  if (hasOAuthCode) {
    return <OAuthCallback />;
  }
  
  // Regular protected route behavior
  return (
    <ProtectedRoute>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Sidebar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            minHeight: '100vh'
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/sites" element={<AllSites />} />
            <Route path="/add-site" element={<AddSite />} />
            <Route path="/sites/:siteId/edit" element={<SiteEdit />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/forms/:id" element={<SiteForms />} />
            <Route path="/site-forms/list" element={<ListSiteForms />} />
            <Route path="/admin/database" element={<AdminDatabase />} />
            <Route path="/admin/disallowed-list" element={<DisallowedList />} />
            <Route path="/admin/site-health" element={<SiteHealth />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/link-review" element={<ContactLinkReview />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/domains" element={<Domains />} />
            <Route path="/domains/:domainId" element={<DomainDetail />} />
            <Route path="/wpengine" element={<WPEnginePage />} />
          </Routes>
        </Box>
      </Box>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginWithTheme />} />
        <Route path="/*" element={
          <CustomThemeProvider>
            <RootHandler />
          </CustomThemeProvider>
        } />
      </Routes>
    </AuthProvider>
  );
}

export default App;